import { useEffect, useMemo, useState } from 'react';

interface Props {
  date: string;
  time: string;
  groomName: string;
  brideName: string;
}

/* '오후 1시 30분' / '13:30' / '오후 2시' 같은 자유 입력을 {h,m} 로 환산 */
function parseWeddingTime(time: string): { h: number; m: number } {
  const raw = (time || '').trim();
  if (!raw) return { h: 12, m: 0 };

  let h = 12;
  let m = 0;
  const hm = raw.match(/(\d{1,2})\s*[:시]\s*(\d{1,2})?/);
  if (hm) {
    h = Number(hm[1]);
    m = hm[2] ? Number(hm[2]) : 0;
  } else {
    const only = raw.match(/(\d{1,2})/);
    if (only) h = Number(only[1]);
  }

  const isPM = /오후|pm/i.test(raw);
  const isAM = /오전|am/i.test(raw);
  if (isPM && h < 12) h += 12;
  if (isAM && h === 12) h = 0;

  if (h < 0) h = 0;
  if (h > 23) h = 23;
  if (m < 0) m = 0;
  if (m > 59) m = 59;
  return { h, m };
}

/* 'YYYY-MM-DD' + 시간 문자열 → 예식 시각 */
function resolveTarget(date: string, time: string): Date | null {
  if (!date) return null;
  const parts = date.split('-').map((n) => Number(n));
  if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) return null;
  const [y, mo, d] = parts;
  const { h, m } = parseWeddingTime(time);
  const dt = new Date(y, mo - 1, d, h, m, 0, 0);
  return Number.isNaN(dt.getTime()) ? null : dt;
}

const pad = (n: number) => String(n).padStart(2, '0');
const startOfDay = (dt: Date) => new Date(dt.getFullYear(), dt.getMonth(), dt.getDate()).getTime();

export default function CountdownBlock({ date, time, groomName, brideName }: Props) {
  const target = useMemo(() => resolveTarget(date, time), [date, time]);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!target) return undefined;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [target]);

  if (!target) return null;

  const diff = target.getTime() - now;
  const dayDiff = Math.round((startOfDay(target) - startOfDay(new Date(now))) / 86400000);
  const passed = diff <= 0;

  const total = Math.max(0, diff);
  const days = Math.floor(total / 86400000);
  const hours = Math.floor((total % 86400000) / 3600000);
  const minutes = Math.floor((total % 3600000) / 60000);
  const seconds = Math.floor((total % 60000) / 1000);

  const dLabel = dayDiff > 0 ? `D-${dayDiff}` : dayDiff === 0 ? 'D-DAY' : 'D-DAY';
  const names = [groomName, brideName].filter(Boolean).join(' · ');

  const units = [
    { v: pad(days), l: '일' },
    { v: pad(hours), l: '시간' },
    { v: pad(minutes), l: '분' },
    { v: pad(seconds), l: '초' },
  ];

  return (
    <section className="inv-countdown">
      <div className="cd-inner">
        <p className="cd-kicker">Countdown</p>
        <p className="cd-title">{names ? `${names}의 결혼식까지` : '우리 결혼식까지'}</p>
        <span className="cd-dday">{dLabel}</span>
        {passed ? (
          <p className="cd-done">두 사람이 부부가 되었습니다. 축하해 주세요.</p>
        ) : (
          <div className="cd-units" role="timer" aria-live="off">
            {units.map((u) => (
              <div className="cd-unit" key={u.l}>
                <span className="cd-num">{u.v}</span>
                <span className="cd-lbl">{u.l}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}